import toast from "react-hot-toast/headless";
import { PointOfSaleCategory } from "../enums/PointOfSaleCategory";
import { AccountParticipant } from "../features/account/models/AccountParticipant";
import { isProvisionalAccount } from "@unleashed/utils/thriveScoreProvisional";
import {
  Product,
  CartItem,
  PrePurchasedItem,
  PurchaseItemViewModel,
  ComboItemViewModel,
  BundleItemViewModel,
  DiscountItem,
  UpgradeTicketViewModel,
  RedeemItemViewModel,
  BookingItemViewModel,
  BaseProduct,
  ComboConfig,
  ProductOptionViewModel,
  ProductModifierViewModel,
  TicketUpgradeDataModel,
} from "../models";
import { areBundleConfigsEqual, getBundleItemQuantities } from "./Bundle";
import { areComboConfigEqual, getTotalPriceForComboConfig } from "./Combo";
import {
  convertComboItemsToPurchaseItemViewModel,
  convertBundleItemsToPurchaseItemViewModel,
} from "./DataConverters";
import {
  accountOwnerAllocatedMembershipIds,
  isMembership,
} from "./MembershipAllocationUtilsHelper";
import {
  getTotalPriceForProductOptions,
  sortProductOptions,
  filterNoneProductModifier,
  areProductOptionsEqual,
} from "./Modifier";
import { isTicket, remainingTickets } from "./Ticket";
import { t } from "i18next";
import { Account } from "../features/account/models/Account";
import { PosState } from "@unleashed-admin/stores/PosSlice";
import { getProductIteemFeeIdOrProductFeeId, isItemFee } from "./MembershipFeesUtils";

export const getProductIdOrTicketIdFromProduct = (
  product:
    | Product
    | CartItem
    | PrePurchasedItem
    | PurchaseItemViewModel
    | ComboItemViewModel
    | BundleItemViewModel
    | DiscountItem
    | UpgradeTicketViewModel
    | undefined
    | null
): number => {
  if (product === undefined || product === null) return 0;

  if ("ticketId" in product && product.ticketId) return product.ticketId;
  if ("productId" in product && product.productId) return product.productId;
  if ("bookingId" in product && product.bookingId) return product.bookingId;
  if ("upgradedTicketId" in product && product.upgradedTicketId)
    return product.upgradedTicketId;
  // BundleId should be checked last as bundleTickets and bundleProducts also have bundleId property
  if ("bundleId" in product && product.bundleId) return product.bundleId;

  return 0;
};

// TODO: Remove the test blob URL and return the fallback image if product image is not available
export const getProductImageSrc = (product: Product, brandSlug: string) => {
  if (product.imageUrl) return product.imageUrl;
  else {
    return getDefaultProductImageByBrand(brandSlug);
  }
};
// TODO: Long-term, this default produce image likely belongs in a configuration that can be manipulated at the brand configuration level
// In the short-term, new brands could be supported by adding to the case statement
export const getDefaultProductImageByBrand = (brand: string) => {
  switch (brand.toLowerCase()) {
  case "urban-air":
    return "/UrbieLogoNew.png";
  default:
    return "/android-chrome-512x512.png";
  }
};

export const arePurchaseItemsEqual = (
  currentPurchaseItems: PurchaseItemViewModel[],
  purchaseItems: PurchaseItemViewModel[]
): boolean => {
  if (
    !currentPurchaseItems ||
    !purchaseItems ||
    currentPurchaseItems.length !== purchaseItems.length
  ) {
    return false;
  }

  return (
    currentPurchaseItems.every((currentItem: PurchaseItemViewModel) => {
      return purchaseItems.some((itemToCheck: PurchaseItemViewModel) => {
        if (currentItem.ticketId === itemToCheck.ticketId) {
          return currentItem.ticketId === itemToCheck.ticketId;
        }
        return (
          currentItem.productId === itemToCheck.productId &&
          areProductOptionsEqual(
            currentItem.productOptions ?? [],
            itemToCheck.productOptions ?? []
          )
        );
      });
    }) &&
    purchaseItems.every((itemToCheck: PurchaseItemViewModel) => {
      return currentPurchaseItems.some((currentItem: PurchaseItemViewModel) => {
        if (currentItem.ticketId) {
          return currentItem.ticketId === itemToCheck.ticketId;
        }
        return (
          currentItem.productId === itemToCheck.productId &&
          areProductOptionsEqual(
            currentItem.productOptions ?? [],
            itemToCheck.productOptions ?? []
          )
        );
      });
    })
  );
};

export type ProductType =
  | Product
  | CartItem
  | PurchaseItemViewModel
  | PrePurchasedItem
  | RedeemItemViewModel
  | ComboItemViewModel
  | BundleItemViewModel
  | BookingItemViewModel
  | UpgradeTicketViewModel;

export const getPriceValue = (product: ProductType): number => {
  if ("price" in product) return product.price ?? 0;
  if ("pricePerUnit" in product) return product.pricePerUnit ?? 0;
  if ("totalPrice" in product) return product.totalPrice ?? 0;
  return 0;
};

export const hasPriceMismatch = (
  current: ProductType,
  toCheck: ProductType
): boolean => {
  return getPriceValue(current) !== getPriceValue(toCheck);
};

export const findSimilarProduct = (
  products: ProductType[],
  productToCheck: ProductType,
  includePrice = true,
  includeUUID = true,
  includeUpgradedCartTicket = true
) => {

  if (isItemFee(productToCheck)) {
    return products.find((currentProduct: ProductType) => {
      return getProductIteemFeeIdOrProductFeeId(currentProduct) === getProductIteemFeeIdOrProductFeeId(productToCheck);
    });
  }  else if (isTicket(productToCheck)) {
    return products.find((currentProduct: ProductType) => {
      if (includePrice && hasPriceMismatch(currentProduct, productToCheck)) {
        return false;
      }

      if (
        includeUpgradedCartTicket &&
        "isUpgradedCartTicket" in currentProduct &&
        "isUpgradedCartTicket" in productToCheck &&
        currentProduct.isUpgradedCartTicket !==
          productToCheck.isUpgradedCartTicket
      ) {
        return false;
      }

      return (
        getProductIdOrTicketIdFromProduct(currentProduct) ===
        getProductIdOrTicketIdFromProduct(productToCheck)
      );
    });
  } else if (isBooking(productToCheck)) {
    const existingBooking = products.find((currentProduct: ProductType) => {
      if ("bookingId" in currentProduct && !!currentProduct.bookingId) {
        return true;
      }
      return false;
    });

    return existingBooking;
  } else {
    return products.find((currentProduct: ProductType) => {
      if (
        getProductIdOrTicketIdFromProduct(currentProduct) !==
        getProductIdOrTicketIdFromProduct(productToCheck)
      ) {
        return false;
      }

      if (
        includeUUID &&
        "uuid" in currentProduct &&
        "uuid" in productToCheck &&
        currentProduct.uuid !== productToCheck.uuid
      ) {
        return false;
      }

      if (includePrice && hasPriceMismatch(currentProduct, productToCheck)) {
        return false;
      }

      if (
        "comboConfigs" in currentProduct &&
        "comboConfigs" in productToCheck &&
        currentProduct.comboConfigs &&
        productToCheck.comboConfigs &&
        currentProduct.comboConfigs.length > 0 &&
        productToCheck.comboConfigs.length > 0
      ) {
        return areComboConfigEqual(
          currentProduct.comboConfigs,
          productToCheck.comboConfigs,
          true
        );
      }

      if (
        "purchaseItems" in currentProduct &&
        "purchaseItems" in productToCheck &&
        currentProduct.purchaseItems &&
        productToCheck.purchaseItems &&
        currentProduct.purchaseItems.length > 0 &&
        productToCheck.purchaseItems.length > 0
      ) {
        return arePurchaseItemsEqual(
          currentProduct.purchaseItems,
          productToCheck.purchaseItems
        );
      }

      if (
        "purchaseItems" in currentProduct &&
        "comboConfigs" in productToCheck &&
        currentProduct.purchaseItems &&
        productToCheck.comboConfigs &&
        currentProduct.purchaseItems.length > 0 &&
        productToCheck.comboConfigs.length > 0
      ) {
        return arePurchaseItemsEqual(
          currentProduct.purchaseItems,
          convertComboItemsToPurchaseItemViewModel(productToCheck.comboConfigs)
        );
      }

      if (
        "purchaseItems" in currentProduct &&
        "bundleConfigs" in productToCheck &&
        currentProduct.purchaseItems &&
        productToCheck.bundleConfigs &&
        currentProduct.purchaseItems.length > 0
      ) {
        return arePurchaseItemsEqual(
          currentProduct.purchaseItems,
          convertBundleItemsToPurchaseItemViewModel(
            productToCheck.bundleConfigs
          )
        );
      }

      if (
        "productOptions" in currentProduct &&
        "productOptions" in productToCheck &&
        currentProduct.productOptions &&
        productToCheck.productOptions &&
        // [] (None selected, or no modifiers) must not fall through to the productId-only match against a line that has modifiers
        (currentProduct.productOptions.length > 0 ||
          productToCheck.productOptions.length > 0)
      ) {
        return areProductOptionsEqual(
          currentProduct.productOptions,
          productToCheck.productOptions
        );
      }

      if (
        "bundleConfigs" in currentProduct &&
        "bundleConfigs" in productToCheck &&
        currentProduct.bundleConfigs &&
        productToCheck.bundleConfigs
      ) {
        return areBundleConfigsEqual(
          currentProduct.bundleConfigs ?? {},
          productToCheck.bundleConfigs ?? {}
        );
      }

      return (
        getProductIdOrTicketIdFromProduct(currentProduct) ===
        getProductIdOrTicketIdFromProduct(productToCheck)
      );
    });
  }
};

export const isBooking = (
  item: CartItem | PrePurchasedItem | PurchaseItemViewModel
) => {
  return item.type === PointOfSaleCategory.Booking;
};

export const isSameProduct = (
  product?: ProductType,
  productToCheck?: ProductType,
  includeUUID = true
) => {
  if (!product || !productToCheck) {
    return false;
  }

  if (isMembership(product)) {
    if (!isMembership(productToCheck)) {
      return false;
    }
  }

  if (isTicket(product)) {
    if (!isTicket(productToCheck)) {
      return false;
    }

    if (
      "isUpgradedCartTicket" in product &&
      "isUpgradedCartTicket" in productToCheck &&
      product.isUpgradedCartTicket !== productToCheck.isUpgradedCartTicket
    ) {
      return false;
    }

    if (hasPriceMismatch(product, productToCheck)) {
      return false;
    }
  } else {
    if (
      includeUUID &&
      "uuid" in product &&
      "uuid" in productToCheck &&
      product.uuid !== productToCheck.uuid
    ) {
      return false;
    }

    if (hasPriceMismatch(product, productToCheck)) {
      return false;
    }

    if (
      "comboConfigs" in product &&
      "comboConfigs" in productToCheck &&
      product.comboConfigs &&
      productToCheck.comboConfigs &&
      product.comboConfigs.length > 0 &&
      productToCheck.comboConfigs.length > 0
    ) {
      return areComboConfigEqual(
        product.comboConfigs,
        productToCheck.comboConfigs,
        true
      );
    }

    if (
      "purchaseItems" in product &&
      "purchaseItems" in productToCheck &&
      product.purchaseItems &&
      productToCheck.purchaseItems &&
      product.purchaseItems.length > 0 &&
      productToCheck.purchaseItems.length > 0
    ) {
      return arePurchaseItemsEqual(
        product.purchaseItems,
        productToCheck.purchaseItems
      );
    }

    if (
      "purchaseItems" in product &&
      "comboConfigs" in productToCheck &&
      product.purchaseItems &&
      productToCheck.comboConfigs &&
      product.purchaseItems.length > 0 &&
      productToCheck.comboConfigs.length > 0
    ) {
      return arePurchaseItemsEqual(
        product.purchaseItems,
        convertComboItemsToPurchaseItemViewModel(productToCheck.comboConfigs)
      );
    }

    if (
      "purchaseItems" in product &&
      "bundleConfigs" in productToCheck &&
      product.purchaseItems &&
      productToCheck.bundleConfigs &&
      product.purchaseItems.length > 0
    ) {
      return arePurchaseItemsEqual(
        product.purchaseItems,
        convertBundleItemsToPurchaseItemViewModel(productToCheck.bundleConfigs)
      );
    }

    if (
      "productOptions" in product &&
      "productOptions" in productToCheck &&
      product.productOptions &&
      productToCheck.productOptions &&
      (product.productOptions.length > 0 ||
        productToCheck.productOptions.length > 0)
    ) {
      return areProductOptionsEqual(
        product.productOptions,
        productToCheck.productOptions
      );
    }

    if (
      "bundleConfigs" in product &&
      "bundleConfigs" in productToCheck &&
      product.bundleConfigs &&
      productToCheck.bundleConfigs
    ) {
      return areBundleConfigsEqual(
        product.bundleConfigs ?? {},
        productToCheck.bundleConfigs ?? {}
      );
    }
  }

  return (
    getProductIdOrTicketIdFromProduct(product) ===
    getProductIdOrTicketIdFromProduct(productToCheck)
  );
};

export const generateUniqueCartItemId = (product: CartItem): string => {
  if (product === undefined || product === null) return "";

  if (isTicket(product)) {
    return `${product.ticketId}`;
  }

  const bundleId = product.bundleConfigs?.bundleTickets
    ?.map((ticket: BaseProduct) => ticket.ticketId)
    .join("-");

  const comboId = product.comboConfigs
    ?.map(
      (comboConfig: ComboConfig) =>
        `${comboConfig.comboConfigId}_${comboConfig.comboItemProducts
          ?.map((product: Product) => {
            const productOptionsIds = product.productOptions
              ?.map(
                (option: ProductOptionViewModel) =>
                  `${option.id}_${option.productModifiers
                    ?.map((modifier: ProductModifierViewModel) => modifier.id)
                    .join(",")}`
              )
              .join("|");

            return `${product.productId}_${product.price}_${productOptionsIds}`;
          })
          .join("-")}`
    )
    .join("-");

  const modifiersId = product?.productOptions
    ?.map(
      (option: ProductOptionViewModel) =>
        `${option.id}_${option.name}_${option.productModifiers
          ?.map(
            (modifier: ProductModifierViewModel) =>
              `${modifier.id}_${modifier.name}_${modifier.price}`
          )
          .join("-")}`
    )
    .join("-");

  if (product.isBundle) {
    return `${product.bundleId}-${bundleId}-${product.price}`;
  }

  if (product.isCombo) {
    return `${product.productId}-${comboId}-${product.price}`;
  }

  return `${product.productId}-${comboId}-${modifiersId}-${product.price}`;
};

export const getTotalPriceForItem = (
  item: CartItem | PrePurchasedItem,
  includeQuantity = true
): number => {
  let productPrice = 0;
  if ("isVariablePrice" in item && item.isVariablePrice) {
    productPrice = item.variablePrice ?? 0;
  } else if ("price" in item) {
    productPrice = item.price ?? 0;
  }
  productPrice += getTotalPriceForProductOptions(item.productOptions || []);
  productPrice += item.isCombo
    ? getTotalPriceForComboConfig(item.comboConfigs || [])
    : 0;
  return includeQuantity ? productPrice * item.quantity : productPrice;
};

export const hasProductPrice = (product?: ProductType) => {
  return product && "price" in product && product.price !== undefined;
};

export const isPaidRedeemItem = (product?: ProductType) => {
  return (
    product &&
    "price" in product &&
    product.price === undefined &&
    getTotalPriceForItem(product) > 0
  );
};

export const updatedPrePurchasedItem = (
  item: PrePurchasedItem,
  productOptions: (
    prePurchasedItem: PrePurchasedItem
  ) => ProductOptionViewModel[]
): PrePurchasedItem => {
  const defaultOptions: ProductOptionViewModel[] = [];
  sortProductOptions(productOptions(item)).forEach(
    (productOption: ProductOptionViewModel) => {
      defaultOptions.push({
        id: productOption.id,
        name: productOption.name,
        isRequired: productOption.isRequired,
        productModifiers: productOption.productModifiers?.slice(0, 1),
      });
    }
  );

  if (defaultOptions.length === 0) return item;

  return {
    ...item,
    price: undefined,
    productOptions: defaultOptions
      .map((o: ProductOptionViewModel) => ({
        ...o,
        productModifiers: filterNoneProductModifier(o.productModifiers),
      }))
      .filter(
        (o: ProductOptionViewModel) => (o.productModifiers?.length ?? 0) > 0
      ),
  } as PrePurchasedItem;
};

// Helper function to update product quantity
export const updateProductQuantity = (
  existingProduct: CartItem,
  cartItem: CartItem
) => {
  existingProduct.quantity = cartItem.quantity;
  existingProduct.quantityFromCheckIn = cartItem.quantityFromCheckIn;
};

export const isCheckoutDisabled = (
  selectedAccount: AccountParticipant | undefined,
  itemsInCart: CartItem[],
  itemsForRedemption: PrePurchasedItem[],
  selectedProduct: Product | undefined,
  checkInParticipants: Account[],
  ticketAllocationData: TicketUpgradeDataModel[],
  maximumVariableItemPriceLimit?: number
) => {
  if (selectedProduct) return true;

  if (isProvisionalAccount(selectedAccount?.accountSourceId)) return true;

  let quantityOfNonPassItems = 0;
  let quantityOfPassItems = 0;
  const quantityOfMemberships =
    itemsInCart?.reduce(
      (acc: number, item: CartItem) =>
        acc + (isMembership(item) ? item.quantity ?? 0 : 0),
      0
    ) || 0;

  for (const product of itemsInCart ?? []) {
    if (
      product.isVariablePrice &&
      (product.variablePrice === undefined ||
        product.variablePrice < 0.01 ||
        product.variablePrice > (maximumVariableItemPriceLimit ?? 1500) ||
        !selectedAccount?.accountId)
    ) {
      return true;
    }
  }

  itemsInCart?.forEach((product: CartItem) => {
    quantityOfNonPassItems =
      quantityOfNonPassItems + (!isTicket(product) ? product.quantity : 0);
    quantityOfPassItems =
      quantityOfPassItems +
      (isTicket(product) && !product.isUpgradedCartTicket
        ? product.quantity
        : 0);

    if (product.isBundle && product.bundleConfigs?.bundleTickets) {
      const ticketsInBundle =
        getBundleItemQuantities(product).bundleTicketQty || 0;
      quantityOfPassItems += ticketsInBundle * product.quantity;
    }
  });

  itemsForRedemption?.forEach((item: PrePurchasedItem) => {
    quantityOfNonPassItems =
      quantityOfNonPassItems + (!isTicket(item) ? item.quantity : 0);
    quantityOfPassItems =
      quantityOfPassItems + (isTicket(item) ? item.quantity : 0);
  });

  if (
    selectedAccount &&
    (quantityOfPassItems > 0 ||
      quantityOfMemberships > 0 ||
      checkInParticipants.length > 0)
  ) {
    // we can select participants for memberships (does not necessarily mean we want to by passes)
    if (quantityOfMemberships > 0 && quantityOfPassItems == 0) {
      if (
        checkInParticipants.some((r) =>
          selectedAccount.waiverParticipants?.some(
            (wp) =>
              wp.id === r.id &&
              (wp.allocatedMembership == null ||
                wp.allocatedMembership == undefined)
          )
        )
      ) {
        return true;
      }

      if (quantityOfMemberships > checkInParticipants.length) {
        return true;
      }

      const allocatedMembershipIds =
        accountOwnerAllocatedMembershipIds(selectedAccount);

      if (allocatedMembershipIds?.length !== quantityOfMemberships) {
        return true;
      }

      return (
        quantityOfMemberships < checkInParticipants.length &&
        quantityOfPassItems < checkInParticipants.length &&
        quantityOfMemberships + quantityOfPassItems <=
          checkInParticipants.length
      );
    }

    if (quantityOfMemberships > 0 && quantityOfPassItems > 0) {
      if (
        quantityOfMemberships + quantityOfPassItems >
        checkInParticipants.length
      ) {
        return true;
      } else {
        return (
          checkInParticipants.length >
          quantityOfMemberships + quantityOfPassItems
        );
      }
    }

    return (
      quantityOfMemberships + quantityOfPassItems != checkInParticipants.length
    );
  }

  return quantityOfNonPassItems <= 0 && ticketAllocationData.length == 0;
};

export const isClearCartAllowed = (
  itemsInCart: CartItem[],
  selectedProduct: Product | undefined
) => {
  if (selectedProduct) return true;

  return itemsInCart.length <= 0;
};

export const isAddProductToCartAllowed = (
  itemsInCart: CartItem[],
  itemsForRedemption: PrePurchasedItem[],
  selectedProduct: Product | undefined,
  checkInParticipants: Account[]
): boolean => {
  if (!selectedProduct?.isBundle) return true;

  if (checkInParticipants.length === 0) {
    toast.error(
      t`Participant(s) must be selected for check-in to purchase a bundle.`
    );
    return false;
  }

  const ticketsInSelectedBundle =
    getBundleItemQuantities(selectedProduct).bundleTicketQty || 0;

  const ticketsRemaining = remainingTickets(
    itemsInCart ?? [],
    itemsForRedemption,
    checkInParticipants.length
  );

  if (ticketsInSelectedBundle <= ticketsRemaining) return true;

  toast.error(
    t`The number of tickets exceeds the number of participants selected for check-in.`
  );
  return false;
};

export const validateItemRedemption = (
  accountOwner?: AccountParticipant,
  product?: Product
) => {
  const prePurchasedItem = accountOwner?.prePurchasedItems?.find(
    (item) =>
      getProductIdOrTicketIdFromProduct(item) ===
      getProductIdOrTicketIdFromProduct(product)
  );

  if (
    prePurchasedItem &&
    !isTicket(prePurchasedItem) &&
    !isMembership(prePurchasedItem) &&
    prePurchasedItem.quantity !== 0
  ) {
    toast.error(
      `Please redeem '${product?.name || ""}' before you move on to purchase it`
    );
    return false;
  }
  return true;
};

export const isPurchasedItem = (item: CartItem) => {
  return !item.isUpgradedCartTicket && !item.isCombo && !item.isBundle && !item.isItemFee && !item.isProductFee;
};

export const updateStateForPrePurchasedItem = (
  state: PosState,
  prePurchasedItem: PrePurchasedItem
) => {
  if (!state.selectedAccount) return;

  const existingItem = prePurchasedItem.uuid
    ? state.itemsForRedemption.find(
      (item: PrePurchasedItem) => item.uuid === prePurchasedItem.uuid
    )
    : (findSimilarProduct(
      state.itemsForRedemption,
      prePurchasedItem
    ) as PrePurchasedItem);

  state.selectedAccount.prePurchasedItems =
    state.selectedAccount?.prePurchasedItems?.map((item: PrePurchasedItem) => {
      if (
        getProductIdOrTicketIdFromProduct(item) ===
        getProductIdOrTicketIdFromProduct(prePurchasedItem)
      ) {
        const redeemedQuantity = existingItem?.quantity ?? 0;
        const newQuantity =
          item.quantity + redeemedQuantity - prePurchasedItem.quantity;
        return { ...item, quantity: newQuantity };
      }
      return item;
    });

  if (existingItem) {
    existingItem.quantity = prePurchasedItem.quantity;
  } else {
    state.itemsForRedemption.push({
      ...prePurchasedItem,
      quantity: prePurchasedItem.quantity,
    });
  }

  state.itemsForRedemption = state.itemsForRedemption.filter(
    (item: PrePurchasedItem) => item.quantity > 0
  );
};

// Helper function to handle product removal from cart
export const removeItemFromCart = (
  state: PosState,
  existingProduct: CartItem
) => {
  state.itemsInCart =
    state.itemsInCart?.filter((item: CartItem) => item !== existingProduct) ??
    [];

  // Remove selected product if it matches
  if (isSameProduct(existingProduct, state.selectedProduct as CartItem)) {
    state.selectedProduct = undefined;
  }

  // TODO AK - Temporary fix for APK-1665 & APK-1677
  // if(state.itemsInCart?.length===0) {
  //   state.selectedAccount = undefined;
  // }
};

/** Returns products sorted by price (lowest first) */
export const sortProductsByPriceAsc = (products: Product[]) =>
  [...(products || [])].sort((a, b) =>
    a.price !== undefined && b.price !== undefined ? a.price - b.price : 0
  );

/** Returns products sorted by price (highest first) */
export const sortProductsByPriceDesc = (products: Product[]) =>
  [...(products || [])].sort(
    (a, b) => getTotalPriceForItem(b) - getTotalPriceForItem(a)
  );

export const sortProductsByIsCombo = (products: Product[]) => {
  return [...(products || [])].sort((a, b) => {
    if (a.isCombo && !b.isCombo) {
      return -1;
    }
    if (!a.isCombo && b.isCombo) {
      return 1;
    }
    return 0;
  });
};
